import React, { useState, useEffect, useContext, useMemo } from 'react'
import { Link } from 'react-router-dom'
import styles from './MicrositeDashboard.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import {
    Store,
    Palette,
    LayoutTemplate,
    Newspaper,
    Globe,
    Rocket,
    Eye,
    TrendingUp,
    FileText,
    MessageSquareMore,
    ExternalLink,
    AlertTriangle,
    CheckCircle2,
    Clock,
    XCircle,
    ArrowRight,
    RefreshCw
} from 'lucide-react'

const STATUS_META = {
    SUCCESS:  { label: 'Live',            tone: 'live',    Icon: CheckCircle2 },
    BUILDING: { label: 'Publishing…',     tone: 'pending', Icon: Clock },
    FAILED:   { label: 'Publish failed',  tone: 'failed',  Icon: XCircle },
    PENDING:  { label: 'Not published',   tone: 'pending', Icon: Clock }
}

const QUICK_ACTIONS = [
    { to: '/microsite/general',   label: 'Edit store details', desc: 'Name, tagline, contact info', Icon: Store },
    { to: '/microsite/theme',     label: 'Customize theme',    desc: 'Colors, fonts, logo',          Icon: Palette },
    { to: '/microsite/landing',   label: 'Edit landing page',  desc: 'Hero, about, testimonials',    Icon: LayoutTemplate },
    { to: '/microsite/blog',      label: 'Write a blog post',  desc: 'Share news and tips',          Icon: Newspaper },
    { to: '/microsite/domains',   label: 'Manage domains',     desc: 'Connect a custom domain',      Icon: Globe },
    { to: '/microsite/publish',   label: 'Publish changes',    desc: 'Push your latest edits live',  Icon: Rocket }
]

const MicrositeDashboard = () => {
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(true)
    const [general, setGeneral] = useState(null)
    const [buildStatus, setBuildStatus] = useState(null)
    const [domain, setDomain] = useState(null)
    const [viewStats, setViewStats] = useState({ totalViews: 0, data: [] })
    const [planInfo, setPlanInfo] = useState(null)

    const post = async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }

    const loadAll = async () => {
        setLoading(true)

        const [generalRes, configRes, buildRes, domainsRes, viewsRes] = await Promise.all([
            post('micrositeGetStoreGeneral'),
            post('micrositeGetStoreConfig'),
            post('micrositeGetBuildStatus'),
            post('micrositeGetDomains'),
            post('micrositeGetPageViewStats', {
                fromDate: new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10),
                toDate: new Date().toISOString().slice(0, 10),
                groupBy: 'page'
            })
        ])

        if (generalRes?.status === 'success') setGeneral(generalRes.response)
        if (configRes?.status === 'success') setPlanInfo(configRes.response?.plan ?? null)
        if (buildRes?.status === 'success') setBuildStatus(buildRes.response)
        if (domainsRes?.status === 'success') {
            const list = domainsRes.response?.data ?? []
            setDomain(list.find((d) => d.isPrimary) ?? list[0] ?? null)
        }
        if (viewsRes?.status === 'success') setViewStats(viewsRes.response)

        setLoading(false)
    }

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    const statusKey = buildStatus?.buildStatus || 'PENDING'
    const statusMeta = STATUS_META[statusKey] || STATUS_META.PENDING

    const planExpiryDays = useMemo(() => {
        if (!planInfo?.expiresOn) return null
        const diff = new Date(planInfo.expiresOn).getTime() - Date.now()
        return Math.ceil(diff / 86400000)
    }, [planInfo])

    const topPage = useMemo(() => {
        if (!viewStats?.data?.length) return null
        return [...viewStats.data].sort((a, b) => b.totalViews - a.totalViews)[0]
    }, [viewStats])

    const previewUrl = general?.subdomain ? `https://${general.subdomain}.lokacipro.com` : null

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading your microsite overview…</span>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            <div className={styles.pageHeader}>
                <div>
                    <h1 className={styles.pageTitle}>Microsite overview</h1>
                    <p className={styles.pageSubtitle}>
                        A quick look at how your salon's website is doing.
                    </p>
                </div>
                {previewUrl && (
                    <a href={previewUrl} target="_blank" rel="noopener noreferrer" className={styles.previewBtn}>
                        <Eye size={14} />
                        View live site
                        <ExternalLink size={12} />
                    </a>
                )}
            </div>

            {/* Plan expiry warning banner */}
            {planExpiryDays !== null && planExpiryDays <= 30 && (
                <div className={styles.warningBanner}>
                    <AlertTriangle size={16} />
                    <span>
                        Your plan expires in <strong>{planExpiryDays} day{planExpiryDays === 1 ? '' : 's'}</strong>.
                        Renew soon to keep your microsite live without interruption.
                    </span>
                </div>
            )}

            {/* Status row */}
            <div className={styles.statusRow}>
                <div className={styles.statusCard}>
                    <div className={styles.statusCardTop}>
                        <span className={styles.statusCardLabel}>Site status</span>
                        <span className={`${styles.statusBadge} ${styles[`tone_${statusMeta.tone}`]}`}>
                            <statusMeta.Icon size={13} />
                            {statusMeta.label}
                        </span>
                    </div>
                    <div className={styles.statusCardMeta}>
                        {buildStatus?.publishedAt
                            ? `Last published ${new Date(buildStatus.publishedAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}`
                            : 'Not published yet'}
                    </div>
                </div>

                <div className={styles.statusCard}>
                    <div className={styles.statusCardTop}>
                        <span className={styles.statusCardLabel}>Primary domain</span>
                    </div>
                    <div className={styles.domainValue}>
                        {domain?.domain || general?.subdomain + '.lokacipro.com' || '—'}
                    </div>
                    {domain && (
                        <div className={styles.statusCardMeta}>
                            SSL: <span className={domain.sslStatus === 'ACTIVE' ? styles.sslGood : styles.sslWarn}>
                                {domain.sslStatus}
                            </span>
                        </div>
                    )}
                </div>

                <div className={styles.statusCard}>
                    <div className={styles.statusCardTop}>
                        <span className={styles.statusCardLabel}>Plan</span>
                    </div>
                    <div className={styles.domainValue}>{planInfo?.planName || 'Free'}</div>
                    {planExpiryDays !== null && (
                        <div className={styles.statusCardMeta}>
                            {planExpiryDays > 0 ? `Renews in ${planExpiryDays} days` : 'Expired'}
                        </div>
                    )}
                </div>
            </div>

            {/* Quick stats */}
            <div className={styles.sectionLabel}>Last 30 days</div>
            <div className={styles.statsGrid}>
                <div className={styles.statTile}>
                    <TrendingUp size={16} className={styles.statIcon} />
                    <div>
                        <div className={styles.statValue}>{viewStats?.totalViews ?? 0}</div>
                        <div className={styles.statLabel}>Total page views</div>
                    </div>
                </div>
                <div className={styles.statTile}>
                    <FileText size={16} className={styles.statIcon} />
                    <div>
                        <div className={styles.statValue}>{topPage?.pageSlug ?? '—'}</div>
                        <div className={styles.statLabel}>Most viewed page</div>
                    </div>
                </div>
                <div className={styles.statTile}>
                    <Newspaper size={16} className={styles.statIcon} />
                    <div>
                        <div className={styles.statValue}>{viewStats?.uniqueVisitors ?? 0}</div>
                        <div className={styles.statLabel}>Unique visitors</div>
                    </div>
                </div>
                <div className={styles.statTile}>
                    <MessageSquareMore size={16} className={styles.statIcon} />
                    <div>
                        <div className={styles.statValue}>{topPage?.bounceCount ?? 0}</div>
                        <div className={styles.statLabel}>Bounces (top page)</div>
                    </div>
                </div>
            </div>

            {/* Quick actions */}
            <div className={styles.sectionLabel}>Quick actions</div>
            <div className={styles.actionsGrid}>
                {QUICK_ACTIONS.map(({ to, label, desc, Icon }) => (
                    <Link key={to} to={to} className={styles.actionCard}>
                        <div className={styles.actionIconWrap}>
                            <Icon size={17} />
                        </div>
                        <div className={styles.actionText}>
                            <div className={styles.actionLabel}>{label}</div>
                            <div className={styles.actionDesc}>{desc}</div>
                        </div>
                        <ArrowRight size={15} className={styles.actionArrow} />
                    </Link>
                ))}
            </div>
        </div>
    )
}

export default MicrositeDashboard